import { Labels } from '../../global/labels';
import { UIElement } from '../ui-element';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import { Child, Children } from '../../decorators/child.decorator';
import {
    insertElements,
    updateElement,
    createElement,
    updateClassList,
} from '../../global/ui-helpers';
import { Digest } from '../../global/digest';
import { UIIcon } from '../icon/ui-icon';
import { browser } from '../../global/browser';
import type {
    UIPaginationLayouts,
    UIPaginationDesktopAlignments,
    UIPaginationDesktopAlignment,
    UIPaginationMobileAlignments,
    UIPaginationMobileAlignment,
    UIPaginationMobileLayouts,
    UIPaginationMobileLayout,
    UIPaginationSizes,
    UIPaginationDesktopLayouts,
    UIPaginationDesktopLayout,
    UIPaginationLayout,
} from './ui-pagination.type';
import type { AttributeValue, ObservedAttributes } from '../ui-element.type';
import type { TranslationLabels } from '../../types/labels.type';
import styles from './ui-pagination.css';
import { NullOr, UndefinedOr } from '../../global/utils.type';
import { runInNextFrame } from '../../global/helpers';

/**
 * @class
 * @memberof SharedComponents
 * @augments UIElement
 * @alias UIPagination
 * @element ui-pagination
 * @classdesc Represents a class for <code>ui-pagination</code> element.
 * @fires event:change
 * @property {number} total {@attr total} Total count of items.
 * @property {number} [pageSize=50] {@attr page-size} Items count for each page.
 * @property {number} [currentPage=1] {@attr current-page} Current page number.
 * @property {number} [rangeBefore=2] {@attr range-before}
 * Pagination items to show before current page.
 * @property {number} [rangeAfter=2] {@attr range-after}
 * Pagination items to show after current page.
 * @property {boolean} [showArrows=false] {@attr show-arrows} Shows arrows in list layout.
 * @property {boolean} [showPageSize=false] {@attr show-page-size}
 * @property {"left" | "center" | "right"} [justifyDesktop="left"] {@attr justify-desktop}
 * Buttons order.
 * {@desc left: Aligns left}
 * {@desc center: Aligns center}
 * {@desc right: Aligns right}
 *  @property {"left" | "right"} [justifyMobile="right"] {@attr justify-mobile}
 * Buttons order for smaller screens.
 * @property {"list" | "dropdown" | "simple" } [layoutDesktop="list"] {@attr layout-desktop}
 * Pagination layout.
 * {@desc list: Pagination buttons}
 * {@desc dropdown: Dropdown select}
 * {@desc simple: Page info with button arrows}
 * @property {"dropdown" | "simple" } [layoutMobile="dropdown"] {@attr layout-mobile}
 * Pagination layout for smaller screens.
 * @property {"small" | "large" } [size="small"] {@attr size} Pagination size for list layout.
 * {@desc small}
 * {@desc large}
 * @property {boolean} [syncControls=true] {@attr sync-controls}
 * two-way binding for currentPage and pageSize.
 * @property {string} layout Layout state.
 * @property {string} justify Justify state.
 * @property {HTMLUListElement} list Shortcut to list element.
 * @property {HTMLSelectElement} select Shortcut to select element.
 * @property {HTMLSelectElement} pageSizeControl Shortcut to page-size select element.
 * @property {HTMLDivElement} info Shortcut to simple layout element.
 * @property {HTMLButtonElement} nextButton Shortcut to next control element.
 * @property {HTMLButtonElement} prevButton Shortcut to previous control element.
 * @property {HTMLButtonElement} active Shortcut to active control element.
 * @property {NodeListOf<HTMLLinkElement>} links Shortcut to link elements.
 * @property {HTMLDivElement} wrapper
 * @property {HTMLLabelElement} currentPageLabel
 * @example
 * <ui-pagination
 *  total="200"
 *  page-size="10"
 *  current-page="2"
 *  range-before="1"
 *  range-after="1"
 *  justify-desktop="right"
 *  justify-mobile="left">
 * </ui-pagination>
 */
@Component({ tag: 'ui-pagination', styles })
export class UIPagination extends UIElement {
    /**
     * @type {UIPaginationLayouts}
     * @deprecated - use LayoutDesktop instead
     */
    static readonly LAYOUT: UIPaginationLayouts = {
        list: 'list',
        select: 'dropdown',
        simple: 'simple',
    };

    /**
     * @type {UIPaginationDesktopAlignments}
     */
    static readonly JustifyDesktop: UIPaginationDesktopAlignments = {
        Left: 'left',
        Center: 'center',
        Right: 'right',
    };

    /**
     * @type {UIPaginationMobileAlignments}
     */
    static readonly JustifyMobile: UIPaginationMobileAlignments = {
        Left: 'left',
        Right: 'right',
    };

    /**
     * @type {UIPaginationDesktopLayouts}
     */
    static readonly LayoutDesktop: UIPaginationDesktopLayouts = {
        List: 'list',
        Dropdown: 'dropdown',
        Simple: 'simple',
    };

    /**
     * @type {UIPaginationMobileLayouts}
     */
    static readonly LayoutMobile: UIPaginationMobileLayouts = {
        Dropdown: 'dropdown',
        Simple: 'simple',
    };

    /**
     * @type {UIPaginationSizes}
     */
    static readonly Size: UIPaginationSizes = {
        Small: 'small',
        Large: 'large',
    };

    /**
     * Localization
     * @type {Labels}
     * @readonly
     */
    static get labels(): TranslationLabels.Labels {
        return Labels.attach('ui-pagination', {
            title: 'Pagination navigation',
            previousPage: 'Previous page',
            nextPage: 'Next page',
            currentPage: 'Current page',
            page: 'Page',
            show: 'Show per page',
        });
    }

    private static _DEFAULT_PAGE_SIZE_LIST?: Array<number>;

    /**
     * Default page size list
     * @type {Array<number>}
     */
    static get DEFAULT_PAGE_SIZE_LIST(): Array<number> {
        return this._DEFAULT_PAGE_SIZE_LIST || [10, 25, 50, 100];
    }

    static set DEFAULT_PAGE_SIZE_LIST(value: Array<number>) {
        this._DEFAULT_PAGE_SIZE_LIST = value;
    }

    /**
     * @type {string[]}
     * @readonly
     */
    static get observedAttributes(): ObservedAttributes {
        return [
            'total',
            'page-size',
            'current-page',
            'range-before',
            'range-after',
            'layout',
            'layout-mobile',
            'layout-desktop',
            'justify',
            'justify-mobile',
            'justify-desktop',
        ];
    }

    @Attribute(Number) total: number = 1;
    @Attribute(Number) pageSize: number = 50;
    @Attribute(Number) currentPage: number = 1;
    @Attribute(Boolean) showPageSize: boolean = false;
    @Attribute(Boolean) showArrows: boolean = false;
    @Attribute(Boolean) syncControls: boolean = true;
    @Attribute(Number) rangeBefore: number = 2;
    @Attribute(Number) rangeAfter: number = 2;
    @Attribute(String) justify?: string;
    @Attribute(String) justifyDesktop: UIPaginationDesktopAlignment =
        UIPagination.JustifyDesktop.Left;
    @Attribute(String) justifyMobile: UIPaginationMobileAlignment =
        UIPagination.JustifyMobile.Right;
    @Attribute(String) layout?: string;
    @Attribute(String) layoutDesktop: UIPaginationDesktopLayout =
        UIPagination.LayoutDesktop.List;
    @Attribute(String) layoutMobile: UIPaginationMobileLayout =
        UIPagination.LayoutMobile.Dropdown;

    @Child('.ui-pagination__select') select?: HTMLSelectElement & {
        hasChangeListener?: boolean;
    };
    @Child('.ui-pagination__list') list?: HTMLUListElement;
    @Child('.ui-pagination__info') info?: HTMLDivElement;
    @Child('.ui-pagination__control.-prev') prevButton!: HTMLButtonElement;
    @Child('.ui-pagination__control.-next') nextButton!: HTMLButtonElement;
    @Child('.-active') active?: HTMLButtonElement;
    @Child('.ui-pagination__page-size-control select')
    pageSizeControl!: HTMLSelectElement & { hasChangeListener?: boolean };
    @Child('.ui-pagination__wrapper') wrapper!: HTMLDivElement;
    @Child('.ui-pagination__page') currentPageLabel?: HTMLLabelElement;
    @Children('.ui-pagination__link') links!: NodeListOf<
        HTMLLinkElement & { hasClickListener?: boolean }
    >;

    private _pageSizeList?: Array<number>;
    private _randomId?: string;

    /**
     * Changes page size select.
     * @type {Array<number>}
     */
    get pageSizeList(): Array<number> {
        return this._pageSizeList || UIPagination.DEFAULT_PAGE_SIZE_LIST;
    }

    set pageSizeList(value: Array<number>) {
        this._pageSizeList = value;
        this.refreshPageSizeList();
    }

    /**
     * @private
     * @returns {string}
     */
    get randomId(): string {
        return this._randomId || (this._randomId = Digest.randomId());
    }

    /**
     * @type {number}
     * @readonly
     */
    get beforeCurrent(): number {
        return this.rangeBefore > 0 ? this.rangeBefore : 1;
    }

    /**
     * @type {number}
     * @readonly
     */
    get afterCurrent(): number {
        return this.rangeAfter > 0 ? this.rangeAfter : 1;
    }

    /**
     * Returns pagination id
     * @type {string}
     * @readonly
     */
    get paginationId(): string {
        return this.id ? this.id : `ui-pagination-${this.randomId}`;
    }

    /**
     * Returns near pages for list layout, all pages for dropdown.
     * @type {Array<number>}
     * @readonly
     */
    get pages(): Array<number> {
        const pages: Array<number> = [];

        if (this.layout === UIPagination.LayoutDesktop.List) {
            let left = Math.max(1, this.currentPage - this.beforeCurrent);
            if (left - 1 === 2) {
                left--;
            }

            let right = Math.min(
                this.currentPage + this.afterCurrent,
                this.pageCount
            );
            if (this.pageCount - right === 2) {
                right++;
            }

            for (let i = left; i <= right; i++) {
                pages.push(i);
            }
        }

        if (this.layout === UIPagination.LayoutDesktop.Dropdown) {
            Array.from({ length: this.pageCount }).map((_, i) => {
                pages.push(i + 1);
            });
        }

        return pages;
    }

    /**
     * Is previous page available.
     * @type {boolean}
     * @readonly
     */
    get hasPrev(): boolean {
        return this.currentPage > 1;
    }

    /**
     * Is next page available.
     * @type {boolean}
     * @readonly
     */
    get hasNext(): boolean {
        return this.currentPage < this.pageCount;
    }

    /**
     * Is first page visible.
     * @type {boolean}
     * @readonly
     */
    get hasFirst(): boolean {
        return this.currentPage >= 2 + this.beforeCurrent;
    }

    /**
     * Is first ellipsis visible.
     * @type {boolean}
     * @readonly
     */
    get hasFirstEllipsis(): boolean {
        return this.currentPage >= this.beforeCurrent + 4;
    }

    /**
     * Is last page visible.
     * @type {boolean}
     * @readonly
     */
    get hasLast(): boolean {
        return this.currentPage <= this.pageCount - (1 + this.afterCurrent);
    }

    /**
     * Is last ellipsis visible.
     * @type {boolean}
     * @readonly
     */
    get hasLastEllipsis(): boolean {
        return this.currentPage < this.pageCount - (2 + this.afterCurrent);
    }

    /**
     * Total page count.
     * @type {number}
     * @readonly
     */
    get pageCount(): number {
        return Math.ceil(this.total / this.pageSize);
    }

    /**
     * Current page total count
     * @type {number}
     * @readonly
     */
    get currentPageCount(): number {
        const itemCount = this.currentPage * this.pageSize - this.pageSize + 1;
        return itemCount >= 0 ? itemCount : 0;
    }

    /**
     * Current layout element.
     * @type {HTMLElement | undefined}
     * @readonly
     */
    get currentLayout(): UndefinedOr<HTMLElement> {
        let currentLayout: UndefinedOr<HTMLElement>;
        if (this.layout === UIPagination.LayoutDesktop.List) {
            currentLayout = this.list;
        } else if (this.layout === UIPagination.LayoutDesktop.Dropdown) {
            currentLayout = this.select;
        } else if (this.layout === UIPagination.LayoutDesktop.Simple) {
            currentLayout = this.info;
        }
        return currentLayout;
    }

    /**
     * Returns label for page
     * @param {number} page
     * @private
     * @returns {string}
     */
    private getPageLabel(page: number): string {
        const isCurrentPage = page === this.currentPage;
        let label = isCurrentPage ? `${UIPagination.labels.currentPage}, ` : '';
        label += `${UIPagination.labels.page} ${page}.`;
        return label;
    }

    /**
     * Changes paginate page. Sends custom event.
     * @param {number} value Selected page.
     * @param {number} [pageSize] Selected page size.
     * @param {boolean} [focus] Should focus on current active paginate item.
     * @private
     */
    private changePage(
        value: number,
        pageSize?: number,
        focus: boolean = false
    ): void {
        pageSize = pageSize !== undefined ? pageSize : this.pageSize;
        if (value < 1 || value > this.pageCount) {
            return;
        }

        if (this.syncControls) {
            this.currentPage = value;

            if (focus) {
                this.active?.focus();
            }
        }
        this.dispatchCustomEvent('change', {
            currentPage: value,
            pageSize: pageSize,
        });
    }

    /**
     * Renders button control.
     * @param {string} label
     * @param {UIIcon} icon
     * @param {string} cssClass
     * @private
     * @returns {HTMLButtonElement}
     */
    private renderControl(
        label: TranslationLabels.Label | string,
        icon: NullOr<UIIcon>,
        cssClass: string
    ): HTMLButtonElement {
        return createElement({
            tagName: 'button',
            classList: ['ui-pagination__control', '-iconed', cssClass].reduce(
                (acc: Record<string, boolean>, cur: string) => {
                    acc[cur] = true;
                    return acc;
                },
                {}
            ),
            attributes: {
                'aria-label': label,
            },
            children: [icon],
        })!;
    }

    /**
     * Updates control disabled attribute.
     * @param {HTMLElement} control
     * @param {boolean} disabled
     * @private
     */
    private updateControlDisabled(
        control: HTMLElement,
        disabled: boolean
    ): void {
        updateElement(control, {
            attributes: {
                disabled: disabled,
            },
        });
    }

    /**
     * Renders icon.
     * @param {string} glyph
     * @private
     * @returns {UIIcon}
     */
    private renderIcon(glyph: string): UIIcon {
        return createElement({
            tagName: 'ui-icon',
            classList: {
                '-navicon': true,
            },
            attributes: {
                glyph: glyph,
                bgcolor: UIIcon.colors.WHITE,
            },
        })!;
    }

    /**
     * Renders button controls.
     * @private
     * @returns {Array<HTMLButtonElement>}
     */
    private renderControls(): Array<HTMLButtonElement> {
        const iconPrev = this.renderIcon('left');
        const buttonPrev = this.renderControl(
            UIPagination.labels.previousPage,
            iconPrev,
            '-prev'
        );
        const iconNext = this.renderIcon('right');
        const buttonNext = this.renderControl(
            UIPagination.labels.nextPage,
            iconNext,
            '-next'
        );
        return [buttonPrev, buttonNext];
    }

    /**
     * Renders layout select option.
     * @param {number} value
     * @private
     * @returns {HTMLOptionElement}
     */
    private renderLayoutSelectOption(value: number): HTMLOptionElement {
        const isCurrentPage = value === this.currentPage;
        return createElement({
            tagName: 'option',
            attributes: {
                'value': value.toString(),
                'selected': isCurrentPage,
                'aria-label': this.getPageLabel(value),
                'aria-current': isCurrentPage ? 'true' : null,
            },
            children: [value.toString()],
        })!;
    }

    /**
     * Renders simple info.
     * @private
     * @returns {HTMLDivElement}
     */
    private renderLayoutInfo(): HTMLDivElement {
        let template = String(this.currentPageCount);
        if (this.pageSize === 1) {
            template += ` / ${this.total}`;
        } else {
            template += `-${Math.min(this.currentPage * this.pageSize, this.total)} / ${this.total}`;
        }
        return createElement({
            tagName: 'div',
            classList: {
                'ui-pagination__info': true,
            },
            children: [
                createElement({
                    tagName: 'p',
                    children: [document.createTextNode(template)],
                }),
            ],
        })!;
    }

    /**
     * Renders page size list.
     * @private
     * @returns {HTMLElement}
     */
    private renderPageSizeControl(): HTMLDivElement {
        const id = `${this.paginationId}-page-size`;
        return createElement({
            tagName: 'div',
            classList: {
                'ui-pagination__page-size-control': true,
            },
            children: [
                createElement({
                    tagName: 'label',
                    attributes: {
                        for: id,
                    },
                    children: [
                        document.createTextNode(
                            String(UIPagination.labels.show)
                        ),
                    ],
                }),
                createElement({
                    tagName: 'select',
                    attributes: {
                        id: id,
                    },
                    children: this.pageSizeList.map(
                        this.renderPageSizeControlOption.bind(this)
                    ),
                }),
            ],
        })!;
    }

    /**
     * Renders page size list option.
     * @param {number} value
     * @private
     * @returns {HTMLElement}
     */
    private renderPageSizeControlOption(value: number): HTMLOptionElement {
        const isSelected = value === this.pageSize;
        const label = `${UIPagination.labels.show} ${value}`;
        return createElement({
            tagName: 'option',
            attributes: {
                'value': value.toString(),
                'selected': isSelected,
                'aria-label': label,
                'aria-current': isSelected ? 'true' : null,
            },
            children: [value.toString()],
        })!;
    }

    /**
     * Renders layout select.
     * @private
     * @returns {HTMLElement}
     */
    private renderLayoutSelect(): HTMLSelectElement {
        const id = `${this.paginationId}-page`;
        return createElement({
            tagName: 'select',
            classList: {
                'ui-pagination__select': true,
            },
            attributes: {
                id: id,
            },
            children: this.pages.map(this.renderLayoutSelectOption.bind(this)),
        })!;
    }

    /**
     * Renders list item.
     * @param {number} value
     * @private
     * @returns {HTMLLIElement}
     */
    private renderListItem(value: number): HTMLLIElement {
        const isCurrentPage = value === this.currentPage;
        return createElement({
            tagName: 'li',
            attributes: {
                value: value,
            },
            children: [
                createElement({
                    tagName: 'button',
                    attributes: {
                        'type': 'button',
                        'aria-label': this.getPageLabel(value),
                        'aria-current': isCurrentPage ? true : null,
                    },
                    classList: {
                        'ui-pagination__link': true,
                        '-active': isCurrentPage,
                    },
                    children: [document.createTextNode(value.toString())],
                }),
            ],
        })!;
    }

    /**
     * Renders ellipsis.
     * @private
     * @returns {HTMLLIElement}
     */
    private renderEllipsis(): HTMLLIElement {
        return createElement({
            tagName: 'li',
            children: [
                createElement(
                    {
                        tagName: 'span',
                        classList: {
                            'ui-pagination__ellipsis': true,
                        },
                        children: ['&hellip;'],
                    },
                    true
                ),
            ],
        })!;
    }

    /**
     * Renders list.
     * @private
     * @returns {HTMLUListElement}
     */
    private renderLayoutList(): HTMLUListElement {
        const items: Array<HTMLElement> = [];
        if (this.hasFirst) {
            items.push(this.renderListItem(1));
        }
        if (this.hasFirstEllipsis) {
            items.push(this.renderEllipsis());
        }
        items.push(...this.pages.map(this.renderListItem.bind(this)));
        if (this.hasLastEllipsis) {
            items.push(this.renderEllipsis());
        }
        if (this.hasLast) {
            items.push(this.renderListItem(this.pageCount));
        }
        return createElement({
            tagName: 'ul',
            classList: {
                'ui-pagination__list': true,
            },
            children: items,
        })!;
    }

    /**
     * Renders current page label
     * @private
     * @returns {HTMLLabelElement}
     */
    private renderCurrentPageLabel(): HTMLLabelElement {
        const id = `${this.paginationId}-page`;
        return this.createElement({
            tagName: 'label',
            attributes: {
                for: id,
            },
            classList: {
                'ui-pagination__page': true,
            },
            children: [
                document.createTextNode(String(UIPagination.labels.page)),
            ],
        }) as HTMLLabelElement;
    }

    /**
     * Renders layout.
     * @private
     * @returns {HTMLElement | undefined}
     */
    private renderLayout(): UndefinedOr<HTMLElement> {
        if (this.layout === UIPagination.LayoutDesktop.List) {
            return this.renderLayoutList();
        } else if (this.layout === UIPagination.LayoutDesktop.Dropdown) {
            return this.renderLayoutSelect();
        } else if (this.layout === UIPagination.LayoutDesktop.Simple) {
            return this.renderLayoutInfo();
        }
        return undefined;
    }

    /**
     * Handles select change.
     * @private
     * @param {Event} event
     */
    private handleChange(event?: Event): void {
        event?.stopPropagation();
        const target = event?.target as HTMLSelectElement;
        const valueString = target.value;
        const value = parseInt(valueString);
        this.changePage(value);
    }

    /**
     * Handles page size select change.
     * @private
     * @param {Event} event
     */
    private handlePageSizeChange(event?: Event): void {
        event?.stopPropagation();
        const target = event?.target as HTMLSelectElement;
        const valueString = target.value;
        const value = parseInt(valueString);
        if (this.syncControls) {
            this.pageSize = value;
        }
        this.refreshPageSizeList();
        this.changePage(this.currentPage, value);
    }

    /**
     * Next page.
     */
    next(): void {
        this.changePage(this.currentPage + 1);
    }

    /**
     * Previous page.
     */
    prev(): void {
        this.changePage(this.currentPage - 1);
    }

    /**
     * Handles paginate click.
     * @private
     * @param {MouseEvent} [event]
     */
    private handleClick(event?: Event): void {
        const target = event?.target as HTMLElement;
        const parent = target?.parentElement as HTMLLIElement & {
            value?: number;
        };
        const value = parent?.value;
        this.changePage(value, this.pageSize, true);
    }

    /**
     * Clears layout from DOM.
     * @param {string} layout
     */
    clearLayout(layout: UIPaginationLayout): void {
        if (layout === UIPagination.LayoutDesktop.List) {
            this.list && this.list.remove();
        } else if (layout === UIPagination.LayoutDesktop.Dropdown) {
            this.select && this.select.remove();
        } else if (layout === UIPagination.LayoutDesktop.Simple) {
            this.info && this.info.remove();
        }
    }

    /**
     * Rebuilds current layout children
     */
    rebuildLayoutChildren(): void {
        const currentLayout = this.currentLayout;
        if (currentLayout) {
            const newLayout = this.renderLayout();
            if (newLayout) {
                this.rebuildChildren.call(
                    currentLayout,
                    Array.from(newLayout.children)
                );
            }
        }
    }

    /**
     * Fires callback when the screen size changes.
     * @private
     */
    private handleSyncAttributes(): void {
        this.syncAttributeLayout();
        this.syncAttributeJustify();
    }

    /**
     * Syncs layout attribute.
     * @private
     */
    private syncAttributeLayout(): void {
        this.layout = browser.mobileMediaMatches()
            ? this.layoutMobile
            : this.layoutDesktop;
    }

    /**
     * Syncs justify attribute.
     * @private
     */
    private syncAttributeJustify(): void {
        this.justify = browser.mobileMediaMatches()
            ? this.justifyMobile
            : this.justifyDesktop;
    }

    /**
     * Wraps element(s) with class
     * @param {Array<HTMLElement>} elements
     * @returns {HTMLElement}
     */
    private wrapElements(elements: Array<NullOr<HTMLElement>>): HTMLElement {
        return createElement({
            tagName: 'div',
            classList: {
                'ui-pagination__wrapper': true,
            },
            children: elements,
        })!;
    }

    /**
     * @inheritDoc
     */
    observeAttributes(
        name: string,
        oldValue: AttributeValue,
        newValue: NullOr<string>
    ): void {
        if (this.state !== 'hydrated') {
            return;
        }
        const currentLayout = this.currentLayout;

        switch (name) {
            case 'range-before':
            case 'range-after':
                // only for list layout
                this.list && this.rebuildLayoutChildren();
                break;
            case 'current-page':
            case 'total':
            case 'page-size':
                currentLayout && this.rebuildLayoutChildren();
                this.updateControlDisabled(this.nextButton, !this.hasNext);
                this.updateControlDisabled(this.prevButton, !this.hasPrev);
                if (this.select) {
                    this.select.value = this.currentPage.toString();
                }
                this.pageSizeControl.value = this.pageSize.toString();
                break;
            case 'layout':
                this.clearLayout(
                    (oldValue as UIPaginationLayout) ||
                        UIPagination.LayoutDesktop.List
                );
                insertElements(this.wrapper, [this.renderLayout()!]);
                this.syncAttributeLayout();
                break;
            case 'justify':
            case 'justify-mobile':
            case 'justify-desktop':
                this.syncAttributeJustify();
                break;
            case 'layout-mobile':
            case 'layout-desktop':
                this.syncAttributeLayout();
                break;
            /* istanbul ignore next */
            default:
                break;
        }

        if (this.layout === 'dropdown') {
            if (this.select !== undefined) {
                updateClassList(this.select, { '-hidden': this.pageCount < 2 });
            }
            if (this.currentPageLabel !== undefined) {
                updateClassList(this.currentPageLabel, {
                    '-hidden': this.pageCount < 2,
                });
            }
        }

        this.subscribeEvents();
        if (this.currentPage > this.pageCount) {
            this.currentPage = Math.max(this.pageCount, 1);
        }
    }

    /**
     * Re-renders pageSizeControl select values
     */
    refreshPageSizeList(): void {
        const children =
            this.renderPageSizeControl().querySelector('select')?.children;
        if (children) {
            this.rebuildChildren.call(
                this.pageSizeControl,
                Array.from(children)
            );
        }
    }

    /**
     * Subscribes event listeners.
     */
    subscribeEvents(): void {
        Array.from(this.links).forEach((link) => {
            if (link.hasClickListener) {
                return;
            }
            updateElement(link, {
                events: {
                    click: this.handleClick.bind(this),
                },
            });
            Object.defineProperty(link, 'hasClickListener', {
                value: true,
            });
        });

        if (this.select && !this.select.hasChangeListener) {
            updateElement(this.select, {
                events: {
                    change: this.handleChange.bind(this),
                },
            });
            Object.defineProperty(this.select, 'hasChangeListener', {
                value: true,
            });
        }

        if (this.pageSizeControl && !this.pageSizeControl.hasChangeListener) {
            updateElement(this.pageSizeControl, {
                events: {
                    change: this.handlePageSizeChange.bind(this),
                },
            });
            Object.defineProperty(this.pageSizeControl, 'hasChangeListener', {
                value: true,
            });
        }
    }

    /**
     * @inheritDoc
     */
    override render(): void {
        this.layout = this.layoutDesktop;
        const controls = this.renderControls();
        const currentPageLabel = this.renderCurrentPageLabel();
        const layout: UndefinedOr<HTMLElement> = this.renderLayout();
        const pageSize = this.renderPageSizeControl();
        /* Temporary exclamation mark assertion to accommodate legacy logic - to be refactored to properly handle undefined layout case! */
        const elements: Array<HTMLElement> = (
            controls as Array<HTMLElement>
        ).concat([currentPageLabel, layout!]);
        this.insertElements([this.wrapElements(elements)]);
        this.insertElements([pageSize]);
        this.updateElement({
            attributes: {
                'layout': this.layout,
                'role': 'navigation',
                'aria-label': UIPagination.labels.title,
            },
        });
    }

    /**
     * @inheritDoc
     */
    override hydrate(): void {
        this.subscribeEvents();
        updateElement(this.nextButton, {
            events: {
                click: this.next.bind(this),
            },
        });
        updateElement(this.prevButton, {
            events: {
                click: this.prev.bind(this),
            },
        });
        this.updateControlDisabled(this.nextButton, !this.hasNext);
        this.updateControlDisabled(this.prevButton, !this.hasPrev);
        browser.mobileMedia.addListener(this.handleSyncAttributes.bind(this));

        // after-hydrate
        runInNextFrame(() => this.handleSyncAttributes());
    }
}
